/**
 * DuplicateComparisonPanel · Sprint 10.
 *
 * Renders when the confidence engine has flagged the current invoice
 * as a duplicate. Loads /api/receiving/{id}/duplicate-comparison and
 * shows both invoices side-by-side with a ✓ / ⚠ per field.
 */
import { useEffect, useState } from "react";
import { CheckCircle, Warning, Info } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { fmtCalendarDateShort } from "@/lib/calendarDate";

const FIELDS = [
  ["supplier",        "Supplier"],
  ["invoice_number",  "Invoice #"],
  ["billing_date",    "Billing Date"],
  ["delivery_date",   "Delivery Date"],
  ["terminal",        "Terminal"],
  ["subtotal",        "Subtotal"],
  ["gst_hst",         "GST / HST"],
  ["total",           "Total"],
  ["product_count",   "Products"],
];

const dateFields = new Set(["billing_date", "delivery_date"]);

export default function DuplicateComparisonPanel({ invoiceId, duplicateOf }) {
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!duplicateOf) return;
    (async () => {
      try {
        const { data } = await api.get(`/receiving/${invoiceId}/duplicate-comparison`);
        setData(data);
      } catch (e) {
        const d = e?.response?.data?.detail;
        toast.error(typeof d === "string" ? d : d?.message || "Could not load comparison.");
      }
    })();
  }, [invoiceId, duplicateOf]);

  if (!duplicateOf) return null;
  if (!data) return (
    <div className="rounded-lg border border-rose-200 bg-rose-50 p-4 text-sm text-rose-900"
         data-testid="duplicate-panel-loading">
      Loading duplicate comparison…
    </div>
  );

  const { current, earlier, diff } = data;
  const totalFields = FIELDS.length;
  const matches = Object.values(diff).filter((v) => v === "match").length;

  const val = (rec, key) => {
    if (key === "product_count") return (rec.products || []).length;
    if (dateFields.has(key)) return fmtCalendarDateShort(rec[key]) || "—";
    if (rec[key] == null || rec[key] === "") return "—";
    return String(rec[key]);
  };

  return (
    <section
      data-testid="duplicate-comparison-panel"
      className="rounded-lg border border-rose-300 bg-white p-4 space-y-3"
    >
      <header className="flex items-baseline justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <Warning size={18} weight="fill" className="text-rose-600" />
          <h3 className="text-sm font-semibold text-rose-900">Duplicate invoice detected</h3>
          <span className="text-[11px] text-slate-500">
            {matches}/{totalFields} fields match the earlier upload.
          </span>
        </div>
        <a
          href={`/receiving/${earlier.id}`}
          className="text-xs font-semibold text-emerald-700 hover:text-emerald-900"
          data-testid="duplicate-open-earlier"
        >
          Open earlier invoice →
        </a>
      </header>

      <div className="overflow-hidden rounded-md border border-slate-200">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500">
            <tr>
              <th className="px-3 py-2 text-left w-1/5">Field</th>
              <th className="px-3 py-2 text-left w-2/5">Earlier upload</th>
              <th className="px-3 py-2 text-left w-2/5">This upload</th>
              <th className="px-3 py-2 text-center w-16">Match</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {FIELDS.map(([key, label]) => {
              const ok = diff[key] === "match";
              return (
                <tr key={key} data-testid={`duplicate-row-${key}`}>
                  <td className="px-3 py-2 text-slate-600">{label}</td>
                  <td className="px-3 py-2 font-mono text-xs text-slate-800">{val(earlier, key)}</td>
                  <td className="px-3 py-2 font-mono text-xs text-slate-800">{val(current, key)}</td>
                  <td className="px-3 py-2 text-center">
                    {ok
                      ? <CheckCircle size={14} weight="fill" className="text-emerald-600 inline" />
                      : <Warning size={14} weight="fill" className="text-amber-600 inline" />}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900 flex items-start gap-2">
        <Info size={14} className="mt-0.5 shrink-0" />
        <div>
          The earlier upload is <b>{earlier.status || "—"}</b>
          {earlier.approved_by ? <> · approved by {earlier.approved_by}</> : null}
          . If this is a truly duplicate PDF, cancel the current upload from the
          lifecycle panel. If the two documents genuinely differ, use "Force" to
          advance the current upload past the duplicate guard with a reason
          recorded on the audit trail.
        </div>
      </div>
    </section>
  );
}
